import { BindingError } from "../BindingError.js";
import equal from "fast-deep-equal/es6/index.js";
class Environment {
  constructor(options) {
    this.options = options;
  }
  static create() {
    return new Environment({
      dimensions: {},
      variables: {},
      parameters: {},
      extensions: /* @__PURE__ */ new Map()
    });
  }
  getSubTree() {
    for (let env = this; env.options.node; env = env.getParent()) {
      const node = env.options.node;
      if (node.type === "subtree-entity" || node.type === "subtree-entity-list") {
        return node;
      }
    }
    throw new BindingError("Not in a SubTree");
  }
  getSubTreeNode() {
    if (!this.options.node) {
      throw new BindingError();
    }
    return this.options.node;
  }
  withSubTree(SubTree) {
    const { parent, ...options } = this.options;
    return new Environment({
      ...options,
      node: SubTree
    });
  }
  withSubTreeChild(node) {
    if (!this.options.node) {
      throw new BindingError(`Cannot call withSubTreeChild without previous call of withSubTree`);
    }
    if (this.options.node.entity.fields.get(node.field.name) !== node.field) {
      throw new BindingError();
    }
    return new Environment({
      ...this.options,
      node,
      parent: this
    });
  }
  hasVariable(key) {
    return key in this.options.variables;
  }
  getVariable(key) {
    if (!(key in this.options.variables)) {
      throw new BindingError(`Variable ${key} not found`);
    }
    return this.options.variables[key];
  }
  getVariableOrElse(key, fallback) {
    return this.options.variables[key] ?? fallback;
  }
  getValue(key) {
    console.warn("Environment.getValue() is deprecated, use Environment.getVariable() or Environment.getParameter() instead.");
    return this.getVariableOrElse(key, void 0) ?? this.getParameterOrElse(key, void 0);
  }
  getValueOrElse(key, fallback) {
    console.warn("Environment.getValueOrElse() is deprecated, use Environment.getVariableOrElse() or Environment.getParameterOrElse() instead.");
    return this.getVariableOrElse(key, void 0) ?? this.getParameterOrElse(key, fallback);
  }
  hasName(key) {
    console.warn("Environment.hasName() is deprecated, use Environment.hasVariable() or Environment.hasParameter() instead.");
    return key in this.options.variables || key in this.options.parameters;
  }
  withVariables(variables) {
    if (variables === void 0) {
      return this;
    }
    const newVariables = { ...this.options.variables };
    for (const [newName, newValue] of Object.entries(variables)) {
      if (newName === "labelMiddleware") {
        throw new BindingError("You cannot pass labelMiddleware to withVariables method. Use withLabelMiddleware instead.");
      }
      const resolvedValue = typeof newValue === "function" ? newValue(this) : newValue;
      if (resolvedValue === void 0) {
        delete newVariables[newName];
      } else {
        newVariables[newName] = resolvedValue;
      }
    }
    return new Environment({
      ...this.options,
      variables: newVariables,
      parent: this
    });
  }
  getAllVariables() {
    return this.options.variables;
  }
  hasParameter(key) {
    return key in this.options.parameters;
  }
  getParameter(key) {
    if (!(key in this.options.parameters)) {
      throw new BindingError(`Parameter ${key} not found`);
    }
    return this.options.parameters[key];
  }
  getParameterOrElse(key, fallback) {
    return this.options.parameters[key] ?? fallback;
  }
  getAllParameters() {
    return this.options.parameters;
  }
  withParameters(parameters) {
    return new Environment({ ...this.options, parameters });
  }
  hasDimension(dimensionName) {
    return dimensionName in this.options.dimensions;
  }
  getDimension(dimensionName) {
    if (!(dimensionName in this.options.dimensions)) {
      throw new BindingError(`Dimension ${dimensionName} does not exist.`);
    }
    return this.options.dimensions[dimensionName];
  }
  getDimensionOrElse(dimensionName, fallback) {
    return this.options.dimensions[dimensionName] ?? fallback;
  }
  getAllDimensions() {
    return this.options.dimensions;
  }
  withDimensions(dimensions) {
    const newDimensions = {
      ...this.options.dimensions,
      ...dimensions
    };
    if (equal(newDimensions, this.options.dimensions)) {
      return this;
    }
    return new Environment({ ...this.options, dimensions: newDimensions });
  }
  getSchema() {
    if (!this.options.schema) {
      throw new BindingError("Schema is not set");
    }
    return this.options.schema;
  }
  withSchema(schema) {
    return new Environment({ ...this.options, schema });
  }
  getParent() {
    if (!this.options.parent) {
      throw new BindingError("There is no parent environment");
    }
    return this.options.parent;
  }
  withExtension(extension, state) {
    return new Environment({
      ...this.options,
      extensions: new Map([...this.options.extensions, [extension, state]])
    });
  }
  getExtension(extension) {
    const state = this.options.extensions.get(extension);
    return extension.create(state, this);
  }
  merge(other) {
    if (other === this) {
      return this;
    }
    if (!equal(this.options.node, other.options.node)) {
      throw new BindingError(`Cannot merge two environments with different tree position.`);
    }
    if (this.options.parameters !== other.options.parameters) {
      throw new BindingError(`Cannot merge two environments with different parameters.`);
    }
    if (this.options.dimensions !== other.options.dimensions) {
      throw new BindingError(`Cannot merge two environments with different dimensions.`);
    }
    if (this.options.extensions !== other.options.extensions) {
      throw new BindingError(`Cannot merge two environments with different extensions.`);
    }
    if (equal(this.options.variables, other.options.variables) && this.options.parent === other.options.parent) {
      return this;
    }
    for (const key in other.options.variables) {
      if (key in this.options.variables && !equal(this.options.variables[key], other.options.variables[key])) {
        throw new BindingError(`Cannot merge two environments with different value of variable ${key}:
` + JSON.stringify(this.options.variables[key]) + "\n" + JSON.stringify(other.options.variables[key]));
      }
    }
    return new Environment({
      ...this.options,
      parent: this.options.parent && other.options.parent ? this.options.parent.merge(other.options.parent) : void 0,
      variables: { ...this.options.variables, ...other.options.variables }
    });
  }
}
((Environment2) => {
  Environment2.createExtension = (create, otherMethods) => {
    return {
      create,
      ...otherMethods
    };
  };
})(Environment || (Environment = {}));
export {
  Environment
};
//# sourceMappingURL=Environment.js.map
